<template>
  <v-dialog v-if="dialogMode" v-model="dialogVisible" max-width="900px" persistent scrollable>
    <v-card class="pa-0 pb-10" tile>
      <v-card-title>
        <h4 class="primary--text">
          {{ contractorType === 'client' ? $t('tslContractors.addClient') : $t('tslContractors.addCarrier') }}
        </h4>
      </v-card-title>
      <v-card-text>
        <Loader v-if="loadingMarketData" />
        <v-form v-else ref="form" v-model="valid">
          <v-row>
          <v-col cols="12" md="6">
            <h5>{{ $t('tslContractors.companyData') }}</h5>
            <v-text-field
              v-model="contractor.companyName"
              :label="$t('tslContractors.companyName')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.vatNumber"
              :label="$t('tslContractors.vatNumber')"
              :rules="[rules.required]"
              outlined
              dense
              :loading="checkingDuplicate"
            />
            <v-alert v-if="duplicateInfo" type="warning" dense outlined class="mb-4 text-body-2">
              {{ duplicateInfo.message }}: <strong>{{ duplicateInfo.contractorName }}</strong>
            </v-alert>
            <v-btn
              color="primary"
              :disabled="isGusEnabled"
              :loading="gusLoading"
              @click="fetchDataFromGUS"
              depressed
              class="mb-4"
            >
              {{ $t('tslContractors.fetchFromGUS') }}
            </v-btn>
            <v-text-field
              v-model="contractor.street"
              :label="$t('tslContractors.street')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.zipCode"
              :label="$t('tslContractors.zipCode')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.city"
              :label="$t('tslContractors.city')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <CountryFlagSelect
              v-model="contractor.country"
              :label="$t('tslContractors.country')"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <h5>{{ $t('tslContractors.contactData') }}</h5>
            <v-text-field
              v-model="contractor.firstName"
              :label="$t('tslContractors.firstName')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.lastName"
              :label="$t('tslContractors.lastName')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.phone"
              :label="$t('tslContractors.phone')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.email"
              :label="$t('tslContractors.email')"
              outlined
              dense
            />
            <v-textarea
              v-if="contractorType === 'client'"
              v-model="contractor.invoiceData"
              :label="$t('tslContractors.invoiceData')"
              outlined
              dense
            />
            <v-textarea
              v-model="contractor.notes"
              :label="$t('tslContractors.notes')"
              outlined
              dense
            />
            <template v-if="contractorType === 'carrier'">
              <v-select
                v-model="contractor.paymentType"
                :items="paymentTypes"
                :label="$t('tslContractors.paymentType')"
                outlined
                dense
              />
              <v-text-field
                v-if="contractor.paymentType === 1"
                v-model.number="contractor.deferredPaymentDays"
                :label="$t('tslContractors.deferredPaymentDays')"
                type="number"
                :rules="[rules.required]"
                outlined
                dense
              />
            </template>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h5>{{ $t('tslContractors.tabs.documents') }}</h5>
            <v-card outlined class="pa-4">
              <v-card-text>
                <v-row v-for="(doc, index) in documents" :key="index" class="mb-4">
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="doc.type"
                      :items="documentTypes"
                      :label="$t('tslContractors.documentType')"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="doc.validTo"
                      :label="$t('tslContractors.validTo')"
                      type="date"
                      :disabled="doc.validIndefinitely"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-checkbox
                      v-model="doc.validIndefinitely"
                      :label="$t('tslContractors.validIndefinitely')"
                      dense
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="doc.description"
                      :label="$t('tslContractors.description')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-file-input
                      v-model="doc.files"
                      :label="$t('tslContractors.files')"
                      multiple
                      outlined
                      dense
                      show-size
                    />
                  </v-col>
                  <v-col cols="12" md="1">
                    <v-btn icon @click="removeDocument(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-btn text color="primary" @click="addDocument">
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t('tslContractors.addDocument') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn color="primary" :disabled="!valid || saving" @click="save" depressed>
              {{ $t('common.save') }}
            </v-btn>
            <v-btn text @click="cancel">
              {{ $t('common.cancel') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
  </v-dialog>
  <v-card v-else class="pa-0 pb-10" tile>
    <v-card-title>
      <h4 class="primary--text">
        {{ contractorType === 'client' ? $t('tslContractors.addClient') : $t('tslContractors.addCarrier') }}
      </h4>
    </v-card-title>
    <v-card-text>
      <Loader v-if="loadingMarketData" />
      <v-form v-else ref="form" v-model="valid">
        <v-row>
          <v-col cols="12" md="6">
            <h5>{{ $t('tslContractors.companyData') }}</h5>
            <v-text-field
              v-model="contractor.companyName"
              :label="$t('tslContractors.companyName')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.vatNumber"
              :label="$t('tslContractors.vatNumber')"
              :rules="[rules.required]"
              outlined
              dense
              :loading="checkingDuplicate"
            />
            <v-alert v-if="duplicateInfo" type="warning" dense outlined class="mb-4 text-body-2">
              {{ duplicateInfo.message }}: <strong>{{ duplicateInfo.contractorName }}</strong>
            </v-alert>
            <v-btn
              color="primary"
              :disabled="isGusEnabled"
              :loading="gusLoading"
              @click="fetchDataFromGUS"
              depressed
              class="mb-4"
            >
              {{ $t('tslContractors.fetchFromGUS') }}
            </v-btn>
            <v-text-field
              v-model="contractor.street"
              :label="$t('tslContractors.street')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.zipCode"
              :label="$t('tslContractors.zipCode')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.city"
              :label="$t('tslContractors.city')"
              :rules="[rules.required]"
              outlined
              dense
            />
            <CountryFlagSelect
              v-model="contractor.country"
              :label="$t('tslContractors.country')"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <h5>{{ $t('tslContractors.contactData') }}</h5>
            <v-text-field
              v-model="contractor.firstName"
              :label="$t('tslContractors.firstName')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.lastName"
              :label="$t('tslContractors.lastName')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.phone"
              :label="$t('tslContractors.phone')"
              outlined
              dense
            />
            <v-text-field
              v-model="contractor.email"
              :label="$t('tslContractors.email')"
              outlined
              dense
            />
            <v-textarea
              v-if="contractorType === 'client'"
              v-model="contractor.invoiceData"
              :label="$t('tslContractors.invoiceData')"
              outlined
              dense
            />
            <v-textarea
              v-model="contractor.notes"
              :label="$t('tslContractors.notes')"
              outlined
              dense
            />
            <template v-if="contractorType === 'carrier'">
              <v-select
                v-model="contractor.paymentType"
                :items="paymentTypes"
                :label="$t('tslContractors.paymentType')"
                outlined
                dense
              />
              <v-text-field
                v-if="contractor.paymentType === 1"
                v-model.number="contractor.deferredPaymentDays"
                :label="$t('tslContractors.deferredPaymentDays')"
                type="number"
                :rules="[rules.required]"
                outlined
                dense
              />
            </template>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <h5>{{ $t('tslContractors.tabs.documents') }}</h5>
            <v-card outlined class="pa-4">
              <v-card-text>
                <v-row v-for="(doc, index) in documents" :key="index" class="mb-4">
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="doc.type"
                      :items="documentTypes"
                      :label="$t('tslContractors.documentType')"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="doc.validTo"
                      :label="$t('tslContractors.validTo')"
                      type="date"
                      :disabled="doc.validIndefinitely"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-checkbox
                      v-model="doc.validIndefinitely"
                      :label="$t('tslContractors.validIndefinitely')"
                      dense
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-text-field
                      v-model="doc.description"
                      :label="$t('tslContractors.description')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-file-input
                      v-model="doc.files"
                      :label="$t('tslContractors.files')"
                      multiple
                      outlined
                      dense
                      show-size
                    />
                  </v-col>
                  <v-col cols="12" md="1">
                    <v-btn icon @click="removeDocument(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <v-btn text color="primary" @click="addDocument">
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t('tslContractors.addDocument') }}
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-btn color="primary" :disabled="!valid || saving" @click="save" depressed>
              {{ $t('common.save') }}
            </v-btn>
            <v-btn text @click="cancel">
              {{ $t('common.cancel') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import Service from '@/services'
import CountryFlagSelect from '@/components/CountryFlagSelect'
import Loader from '@/components/Loader'

export default {
  name: 'AddTSLContractor',
  components: {
    CountryFlagSelect,
    Loader
  },
  props: {
    dialogMode: {
      type: Boolean,
      default: false
    },
    contractorTypeOverride: {
      type: Number,
      default: null
    },
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    let type
    if (this.contractorTypeOverride !== null) {
      type = this.contractorTypeOverride === 1 ? 'client' : 'carrier'
    } else {
      type = this.$route.params.type?.toLowerCase()
    }
    return {
      valid: false,
      saving: false,
      loadingMarketData: false,
      contractorType: type,
      dialogVisible: this.value,
      contractor: {
        type: this.contractorTypeOverride !== null ? this.contractorTypeOverride : (type === 'client' ? 1 : 2),
        companyName: '',
        vatNumber: '',
        street: '',
        zipCode: '',
        city: '',
        country: null,
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        invoiceData: '',
        notes: '',
        paymentType: null,
        deferredPaymentDays: null
      },
      paymentTypes: [
        { text: this.$t('dateOfPayment.none'), value: 0 },
        { text: this.$t('dateOfPayment.deferred'), value: 1 },
        { text: this.$t('dateOfPayment.paymentOnUnloading'), value: 2 },
        { text: this.$t('dateOfPayment.paymentInAdvance'), value: 3 }
      ],
      documents: [],
      duplicateInfo: null,
      checkingDuplicate: false,
      vatNumberTimeout: null,
      gusLoading: false,
      rules: {
        required: value => !!value || this.$t('tslContractors.validationRequired')
      }
    }
  },
  watch: {
    'contractor.vatNumber' (newVal) {
      clearTimeout(this.vatNumberTimeout)
      this.duplicateInfo = null

      if (newVal && newVal.length >= 8) {
        this.vatNumberTimeout = setTimeout(() => {
          this.checkDuplicateVatNumber()
        }, 1000)
      }
    },
    value (newVal) {
      this.dialogVisible = newVal
    },
    dialogVisible (newVal) {
      this.$emit('input', newVal)
      if (!newVal) {
        this.$emit('close')
      }
    }
  },
  computed: {
    documentTypes () {
      if (this.contractorType === 'client') {
        return [
          { text: this.$t('tslContractors.documentCategories.vat'), value: 5 },
          { text: this.$t('tslContractors.documentCategories.certificates'), value: 6 },
          { text: this.$t('tslContractors.documentCategories.other'), value: 7 }
        ]
      } else {
        return [
          { text: this.$t('tslContractors.documentCategories.insurance'), value: 8 },
          { text: this.$t('tslContractors.documentCategories.license'), value: 9 },
          { text: this.$t('tslContractors.documentCategories.vat'), value: 10 },
          { text: this.$t('tslContractors.documentCategories.certificates'), value: 11 },
          { text: this.$t('tslContractors.documentCategories.other'), value: 12 }
        ]
      }
    },
    isGusEnabled () {
      const regex = /^[0-9]{8,14}$/
      return !regex.test(this.contractor.vatNumber) || this.gusLoading
    }
  },
  async created () {
    // Sprawdź czy to kopiowanie z kontrahenta giełdy (stary mechanizm)
    if (this.$route.query.fromMarket === 'true' && this.$route.query.marketCompanyId) {
      await this.loadMarketCompanyData()
    } else {
      // Sprawdź czy są dane pre-fill z dialogu "Wystaw zlecenie"
      const preFill = this.$store.getters.tslOrderPreFill
      if (preFill && preFill.pendingOrderCreation && preFill.offerCompanyData) {
        this.applyOfferCompanyData(preFill.offerCompanyData)
      }
    }
  },
  methods: {
    applyOfferCompanyData (companyData) {
      this.contractor = {
        ...this.contractor,
        companyName: companyData.companyName || '',
        vatNumber: companyData.vatNumber || '',
        street: companyData.street || '',
        zipCode: companyData.zipCode || '',
        city: companyData.city || '',
        country: companyData.country || null,
        phone: companyData.phone || '',
        email: companyData.email || ''
      }
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate()
        }
      })
    },
    async checkDuplicateVatNumber () {
      this.checkingDuplicate = true
      try {
        const { data } = await Service.get('/TSLContractor/CheckDuplicateVatNumber', {
          vatNumber: this.contractor.vatNumber
        })
        if (data.exists) {
          this.duplicateInfo = {
            message: this.$t('tslContractors.duplicateNipWarning'),
            contractorName: data.contractorName
          }
        }
      } catch (e) {
        // Silent fail - nie przerywamy pracy użytkownika
      } finally {
        this.checkingDuplicate = false
      }
    },
    async fetchDataFromGUS () {
      this.gusLoading = true
      try {
        const { data } = await Service.get('/ExternalApi/GetGusByNip', {
          nip: this.contractor.vatNumber
        })

        this.contractor = {
          ...this.contractor,
          companyName: data.name,
          city: data.city,
          zipCode: data.postalCode,
          street: `${data.street} ${data.streetNumber}`,
          country: 'PL'
        }

        this.$nextTick(() => {
          this.$refs.form.validate()
        })
      } catch (e) {
        this.$store.dispatch('setErrorNotification', this.$t('tslContractors.gusError'))
      } finally {
        this.gusLoading = false
      }
    },
    addDocument () {
      this.documents.push({
        type: null,
        validTo: null,
        validIndefinitely: false,
        description: '',
        files: []
      })
    },
    removeDocument (index) {
      this.documents.splice(index, 1)
    },
    async save () {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        const formData = new FormData()

        // Add contractor data
        formData.append('Contractor.Type', this.contractor.type)
        formData.append('Contractor.CompanyName', this.contractor.companyName)
        formData.append('Contractor.VatNumber', this.contractor.vatNumber)
        formData.append('Contractor.Street', this.contractor.street)
        formData.append('Contractor.ZipCode', this.contractor.zipCode)
        formData.append('Contractor.City', this.contractor.city)
        formData.append('Contractor.Country', this.contractor.country)

        // Add optional contact data
        if (this.contractor.firstName) formData.append('Contractor.FirstName', this.contractor.firstName)
        if (this.contractor.lastName) formData.append('Contractor.LastName', this.contractor.lastName)
        if (this.contractor.phone) formData.append('Contractor.Phone', this.contractor.phone)
        if (this.contractor.email) formData.append('Contractor.Email', this.contractor.email)

        // Add optional business fields
        if (this.contractor.invoiceData) formData.append('Contractor.InvoiceData', this.contractor.invoiceData)
        if (this.contractor.notes) formData.append('Contractor.Notes', this.contractor.notes)
        if (this.contractor.paymentType !== null) formData.append('Contractor.PaymentType', this.contractor.paymentType)
        if (this.contractor.deferredPaymentDays !== null) formData.append('Contractor.DeferredPaymentDays', this.contractor.deferredPaymentDays)

        // Add documents
        this.documents.forEach((doc, docIndex) => {
          if (doc.type && doc.files && doc.files.length > 0) {
            formData.append(`Contractor.Documents[${docIndex}].Type`, doc.type)
            formData.append(`Contractor.Documents[${docIndex}].ValidIndefinitely`, doc.validIndefinitely)

            if (!doc.validIndefinitely && doc.validTo) {
              formData.append(`Contractor.Documents[${docIndex}].ValidTo`, doc.validTo)
            }

            if (doc.description) {
              formData.append(`Contractor.Documents[${docIndex}].Description`, doc.description)
            }

            doc.files.forEach((file) => {
              formData.append(`Contractor.Documents[${docIndex}].Files`, file)
            })
          }
        })

        // Send everything in one request
        const { data } = await Service.post('/TSLContractor/CreateContractor', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.$store.dispatch('setSuccessNotification', this.$t('tslContractors.created'))

        if (this.dialogMode) {
          // In dialog mode, emit created event with contractor data
          this.$emit('created', {
            id: data.id,
            companyName: this.contractor.companyName,
            vatNumber: this.contractor.vatNumber,
            city: this.contractor.city,
            displayName: `${this.contractor.companyName} (${this.contractor.vatNumber})`
          })
          this.dialogVisible = false
        } else {
          // In page mode: check if we arrived from "Wystaw zlecenie" flow
          const preFill = this.$store.getters.tslOrderPreFill
          if (preFill && preFill.pendingOrderCreation) {
            this.$store.commit('SET_TSL_ORDER_PRE_FILL', {
              ...preFill,
              contractorId: data.id,
              pendingOrderCreation: false
            })
            this.$router.push({ name: 'AddTSLOrder' })
          } else {
            this.$router.push('/tsl/contractors')
          }
        }
      } catch (e) {
        console.error('Error creating contractor:', e)
        const errorMessage = e.response?.data?.errors
          ? Object.values(e.response.data.errors).flat().join(', ')
          : e.response?.data?.title || e.response?.data || 'Error creating contractor'
        this.$store.dispatch('setErrorNotification', errorMessage)
        // Stay on form to allow user to fix errors
      } finally {
        this.saving = false
      }
    },
    cancel () {
      if (this.dialogMode) {
        this.dialogVisible = false
        this.$emit('close')
      } else {
        const preFill = this.$store.getters.tslOrderPreFill
        if (preFill && preFill.pendingOrderCreation) {
          this.$store.commit('SET_TSL_ORDER_PRE_FILL', null)
        }
        this.$router.push('/tsl/contractors')
      }
    },
    async loadMarketCompanyData () {
      this.loadingMarketData = true
      try {
        const { data } = await Service.get('/Company/GetCompanyDetails', {
          companyId: this.$route.query.marketCompanyId
        })

        // Wypełnij formularz danymi z kontrahenta giełdy
        this.contractor = {
          ...this.contractor,
          companyName: data.companyName || '',
          vatNumber: data.vatNumber || '',
          street: data.street || '',
          zipCode: data.zipCode || '',
          city: data.city || '',
          country: data.country || null,
          phone: data.phone || '',
          email: data.email || ''
        }

        // Waliduj formularz aby pokazać czy dane są kompletne
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.$refs.form.validate()
          }
        })
      } catch (e) {
        this.$store.dispatch('setErrorNotification',
          this.$t('common.error') || 'Błąd podczas pobierania danych')
      } finally {
        this.loadingMarketData = false
      }
    }
  }
}
</script>
